import React, { createContext, useRef, useState, useEffect } from 'react'
import { useTenant } from '@emerald-works-nova/auth'
import { FALLBACK_VARIANTS, ADS_VARIANT_STORAGE_KEY, selectWeightedVariant } from './google-ad-variant'
import consts from '../../consts'
import { useGoogleAdsense } from '../../hooks'

export const GoogleAdContext = createContext()

export const GoogleAdProvider = ({ children }) => {
  const tenant = useTenant()
  const { adsSettings } = tenant || {}
  const selectedVariantRef = useRef(null)
  const variantsEnabledRef = useRef(adsSettings?.variantsEnabled)

  const pushedAds = useRef(new Set())
  const [previousRoute, setPreviousRoute] = useState(null)
  const [adsRequestSent, setAdsRequestSent] = useState(false)

  const { useAdsense } = useGoogleAdsense()
  const shouldBeAds = useAdsense

  useEffect(() => {
    // PENG-2310 Google Auto Ads (Vignette Ads) are appearing for paid consumer users
    const script = document.querySelector('script[src*="adsbygoogle.js"]')
    console.log('*** Google Adsense [script] ***', script)
    // Remove the Google Adsense script if ads should not be displayed
    if (!shouldBeAds) {
      if (script) {
        script.remove()
        console.log('*** Google Adsense [script] removed ***')
      }
      // Remove any existing Google Adsense auto ads from the page (scrub of injected ads)
      const adSelectors = ['.adsbygoogle', 'iframe[src*="ads"]', 'ins.adsbygoogle', 'div[id^="google_ads_iframe"]', 'div[id*="google_ads_iframe"]']
      let removedCount = 0
      adSelectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(ad => {
          ad.remove()
          removedCount++
        })
      })
      console.log(`*** Google Adsense [${removedCount} auto ads removed] ***`)
    }
    /**
     * If the logic above is not enough, because Google could inject auto ads before removing the script
     * the next action is to stop add the script in the HTML directly, and inject the script dynamically only when shouldBeAds is true.
     */
  }, [shouldBeAds])

  if (consts.DEFAULT_CLUB_PK === tenant.pk && !selectedVariantRef.current) {
    const availableVariants = variantsEnabledRef ? adsSettings?.variants : FALLBACK_VARIANTS

    if (!variantsEnabledRef.current) {
      // When Ads Variants are disabled, remove any stored variant and use the default one without setting it in localStorage
      const storedVariant = localStorage.getItem(ADS_VARIANT_STORAGE_KEY)
      if (storedVariant) {
        localStorage.removeItem(ADS_VARIANT_STORAGE_KEY)
        console.log('[Variants Disabled] Removed stored variant:', storedVariant)
      }
      selectedVariantRef.current = { key: 'DEFAULT', type: 'display' }
      console.log('[Variants Disabled] Using DEFAULT variant')
    } else {
      const storedKey = localStorage.getItem(ADS_VARIANT_STORAGE_KEY)
      const matchedStoredVariant = availableVariants.find(v => v.key === storedKey)

      if (matchedStoredVariant) {
        selectedVariantRef.current = matchedStoredVariant
        console.log('[Variants Enabled][Keep variant] Loaded from storage:', storedKey)
      } else {
        const selected = selectWeightedVariant(availableVariants)
        localStorage.setItem(ADS_VARIANT_STORAGE_KEY, selected.key)
        selectedVariantRef.current = selected
        console.log('[Variants Enabled][New variant] Selected and stored:', selected.key)
      }
    }
  }

  const sendAdsRequest = () => {
    console.log('Page Loaded, sending Ads Request...')
    ;(window.adsbygoogle = window.adsbygoogle || []).pauseAdRequests = 0
    setAdsRequestSent(true)
  }

  const getAdStatus = async () => {
    const isDev = ['mt3.dev', 'ewnova.dev', 'localhost'].some((host) => window.location.hostname.endsWith(host))
    const adSelector = isDev ? 'div.adsbygoogle' : 'ins.adsbygoogle'

    // Variants Config
    const initialDelay = adsSettings?.variantsConfig?.initialDelay || 1000
    const timeout = adsSettings?.variantsConfig?.timeout || 2000
    console.log(`[getAdStatus] Using initialDelay: ${initialDelay}ms - timeout: ${timeout}ms`)

    const waitForStatuses = () => {
      return new Promise((resolve) => {
        const processedAds = new Set()
        const filled = []
        const unfilled = []
        const blank = []

        // STEP 1: wait initialDelay before starting check
        setTimeout(() => {
          const adElements = document.querySelectorAll(adSelector)

          // STEP 2: check all ad elements once for filled/unfilled
          for (const ad of adElements) {
            const slotId = ad.getAttribute('data-ad-slot')
            const status = ad.getAttribute('data-ad-status')

            if (!slotId) {
              console.log(`[getAdStatus] Ad without slotId: divClass: ${ad.className}`)
              continue
            }
            if (status === 'filled') {
              filled.push({
                slotId,
                divClass: ad.className,
                adStatus: 'filled'
              })
              processedAds.add(ad.className)
            } else if (status === 'unfilled') {
              unfilled.push({
                slotId,
                divClass: ad.className,
                adStatus: 'unfilled'
              })
              processedAds.add(ad.className)
            }
          }

          // STEP 3: wait remaining time (timeout), verify againg and then treating rest as blank
          setTimeout(() => {
            const finalAdElements = document.querySelectorAll(adSelector)
            for (const ad of finalAdElements) {
              if (processedAds.has(ad.className)) continue

              const slotId = ad.getAttribute('data-ad-slot')
              if (!slotId) {
                console.log(`[getAdStatus] after [timeout]:${timeout} - Ad without slotId: divClass: ${ad.class}`)
                continue
              }
              const status = ad.getAttribute('data-ad-status')

              if (status === 'filled') {
                filled.push({
                  slotId,
                  divClass: ad.className,
                  adStatus: 'filled'
                })
                processedAds.add(ad.className)
              } else if (status === 'unfilled') {
                unfilled.push({
                  slotId,
                  divClass: ad.className,
                  adStatus: 'unfilled'
                })
                processedAds.add(ad.className)
              } else {
                blank.push({
                  slotId,
                  divClass: ad.className,
                  adStatus: 'blank'
                })
                processedAds.add(ad.className)
              }
            }

            const reportAds = {
              filled,
              unfilled,
              blank,
              pageUrl: window.location.href,
              variant: selectedVariantRef.current?.key || 'DEFAULT'
            }
            resolve({ adsCoverage: reportAds, adsUnits: processedAds })
          }, timeout)
        }, initialDelay)
      })
    }

    return await waitForStatuses()
  }

  return (
    <GoogleAdContext.Provider
      value={{
        pushedAds,
        previousRoute,
        setPreviousRoute,
        sendAdsRequest,
        adsRequestSent,
        setAdsRequestSent,
        getAdStatus,
        adsSettings,
        selectedVariant: selectedVariantRef.current,
        variantsEnabled: variantsEnabledRef.current
      }}
    >
      {children}
    </GoogleAdContext.Provider>
  )
}
